import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { Toaster } from 'sonner'

import { BrowserRouter } from 'react-router-dom';

import { useIntercom } from 'react-use-intercom';

import Smartlook from 'smartlook-client'

import { createTheme, StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { PropsFromRedux } from '../containers/AppContainer';
// import BlockNumberIndicator from './BlockNumberIndicator';
import { useWindowSize } from '../hooks';
import { AppConfig } from '../constants';
import { IPropyCookieValue } from '../interfaces';

import PageContainer from './PageContainer';

import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

interface IAnalyticsWrapperProps {
  propyCookieValue: IPropyCookieValue;
  children: any;
}

const AnalyticsWrapper = (props: IAnalyticsWrapperProps) => {

  const gtmParams = {
    id: AppConfig.GOOGLE_GTAG_ID
  };

  const {
    children,
    propyCookieValue,
  } = props;

  if(propyCookieValue?.services?.analytics?.indexOf('gtm') > -1) {
    return (
      <GTMProvider state={gtmParams}>
        {children}
      </GTMProvider>
    )
  } else {
    return (
      <>
        {children}
      </>
    )
  }
}

const App = (props: PropsFromRedux) => {

  const {
    propyCookieValue,
    setConsideredMobile,
    setConsideredMedium,
  } = props;

  const smartlookInitialized = useRef(false);
  const intercomInitialized = useRef(false);

  const {
    boot,
    shutdown,
    // hide,
    // show,
    // update
  } = useIntercom();

  useEffect(() => {
    const hasSmartlookConsent = propyCookieValue?.services?.analytics?.includes('smartlook');
    
    if (AppConfig.SMARTLOOK_ID && hasSmartlookConsent && !smartlookInitialized.current) {
      Smartlook.init(AppConfig.SMARTLOOK_ID);
      smartlookInitialized.current = true;
    } else if (!hasSmartlookConsent && smartlookInitialized.current) {
      Smartlook.disable();
      smartlookInitialized.current = false;
    }

    return () => {
      if (smartlookInitialized.current) {
        Smartlook.disable();
        smartlookInitialized.current = false;
      }
    };
  }, [propyCookieValue]);

  useEffect(() => {
    const hasIntercomConsent = propyCookieValue?.services?.functional?.includes('intercom');

    if (hasIntercomConsent && !intercomInitialized.current) {
      boot();
      intercomInitialized.current = true;
    } else if (!hasIntercomConsent && intercomInitialized.current) {
      shutdown();
      intercomInitialized.current = false;
    }

    return () => {
      if (intercomInitialized.current) {
        shutdown();
        intercomInitialized.current = false;
      }
    };
  }, [propyCookieValue, boot, shutdown]);

  const windowSize = useWindowSize();

  useLayoutEffect(() => {
    let sizeConsiderMobile = 1100;
    let sizeConsideredMedium = 1280;
    if (windowSize.width && (windowSize.width <= sizeConsiderMobile)) {
      setConsideredMobile(true);
    }else{
      setConsideredMobile(false);
    }
    if (windowSize.width && (windowSize.width <= sizeConsideredMedium)) {
      setConsideredMedium(true);
    }else{
      setConsideredMedium(false);
    }
  }, [windowSize.width, windowSize.height, setConsideredMobile, setConsideredMedium])

  const theme = React.useMemo(
    () =>
      createTheme({
        typography: {
          h1: {
            fontFamily: "Inter",
            lineHeight: 1,
          },
          h2: {
            fontFamily: "Inter",
            lineHeight: 1,
          },
          h3: {
            fontFamily: "Inter",
            lineHeight: 1,
            fontSize: '2.5rem',
          },
          h4: {
            lineHeight: 1,
          },
          fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`
        },
        components: {
          MuiCardContent :{
            styleOverrides: {
              root:{
                borderRadius: 15,
                overflow: 'auto',
                "&:last-child": {
                  paddingBottom: '16px'
                },
              }
            }
          },
          MuiPaper: {
            styleOverrides: {
              root:{
                borderRadius: 15,
              }
            }
          },
          MuiButton: {
            styleOverrides: {
              root: {
                // textTransform: 'none',
                fontSize: '1rem',
                boxShadow: 'none',
                borderRadius: '20px',
                "&:hover": {
                  boxShadow: 'none',
                },
              },
              outlinedSizeMedium: {
                height: 36,
              },
              contained: {
                color: 'white',
              },
              outlined: {
                backgroundColor: 'white'
              }
            }
          },
          MuiFab: {
            styleOverrides: {
              root: {
                // textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              },
            }
          }
        },
        palette: {
          mode: props.darkMode ? 'dark' : 'light',
          primary: {
            main: "#37a6fb",
          },
          secondary: {
            main: "#414141",
          },
          ...(props.darkMode && {
            background: {
              default: "#131313",
              paper: "#2b2b2b"
            }
          })
        },
      }),
    [props.darkMode],
  );

  return (
    <AnalyticsWrapper propyCookieValue={propyCookieValue}>
      <BrowserRouter>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <CssBaseline/>
              <Toaster richColors position="bottom-center" />
              <PageContainer/>
              {/* <BlockNumberIndicator/> */}
            </ThemeProvider>
          </StyledEngineProvider>
      </BrowserRouter>
    </AnalyticsWrapper>
  );
}

export default App;
